import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { db } from '../firebase';
import { collection, getDocs, setDoc, doc } from 'firebase/firestore';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  Avatar,
  Button,
  Grid,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

const API_URL = 'https://sorteosrd.com/api/sorteosrd-results/217XUR6ivy8A1NdI4SKcnAa9YGZRV7w3';

const filteredLotteries = [
  'Florida Lottery 1:30 PM',
  'Florida 10:50 PM',
  'King Lottery 12:30 PM',
  'King Lottery 7:30 PM',
  'New York Lottery 3:30 PM',
  'New York Lottery 10:30 PM',
];

const validDescriptions = [
  'Loto Real 12:55 PM',
  'La Suerte Dominicana 12:30PM',
  'La Suerte 6:00 PM',
  'La Primera 12:00PM',
  'La Primera 8:00 PM',
  'Quiniela Loteka 7:55 PM',
  'Quiniela LoteDom 5:55 PM',
  'Anguila 10:00 AM',
  'Anguila 1:00 PM',
  'Anguila 6:00 PM',
  'Anguila 9:00 PM',
  'Loteria Nacional 2:30 PM Gana Más',
  'Loteria Nacional 8:50 PM',
  'Quiniela Pale Leidsa 8:55 PM / Dom 3:00 PM',
];

// Utilidades de fecha usando la zona horaria de Santo Domingo
const getCurrentDateInRD = () => {
  const rdTime = new Date().toLocaleString('en-US', { timeZone: 'America/Santo_Domingo' });
  return rdTime.split(',')[0];
};

const normalizeDate = (dateInput) => {
  const date = new Date(dateInput);
  return date.toLocaleDateString('en-US', { timeZone: 'America/Santo_Domingo' });
};

const isSameDate = (date1, date2) => {
  const d1 = new Date(date1).toLocaleDateString('en-CA', { timeZone: 'America/Santo_Domingo' });
  const d2 = new Date(date2).toLocaleDateString('en-CA', { timeZone: 'America/Santo_Domingo' });
  return d1 === d2;
};

// Procesa resultados de la API filtrándolos por fecha y descripción
const processApiResults = (apiData, currentDate) => {
  if (!Array.isArray(apiData)) return [];

  return apiData
    .filter(result => 
      result.descripcion && 
      result.created_at && 
      isSameDate(result.created_at, currentDate)
    )
    .map(result => {
      const isFilteredLottery = filteredLotteries.includes(result.descripcion);
      const numeros = isFilteredLottery
        ? [result.num1.substring(1, 3), result.num2, result.num3].map(String)
        : [result.num1, result.num2, result.num3].map(String);
      return { numeros, descripcion: result.descripcion, created_at: result.created_at };
    });
};

// Agrupa las predicciones por usuario y procesa sus números
const groupPredictionsByUser = (predictions, currentDate) => {
  return predictions
    .filter(pred => pred.timestamp === currentDate)
    .reduce((map, { username, numbers, timestamp }) => {
      if (!map[username]) {
        map[username] = [];
      }
      const uniqueNumbers = [...new Set(numbers)];
      const userNumbers = uniqueNumbers.map((num) => {
        const str = num.toString();
        return {
          original: str,
          flipped: str.split('').reverse().join(''),
        };
      });
      map[username].push({ userNumbers, predictionDate: timestamp });
      return map;
    }, {});
};

// Calcula las estadísticas combinadas a partir de los datos guardados, predicciones y resultados de la API
const calculateStatistics = (savedStatistics, predictionsMap, apiResults) => {
  const statsMap = savedStatistics.reduce((map, stat) => {
    map[stat.username] = { ...stat, dailyScore: 0, aciertosToday: [] };
    return map;
  }, {});

  // Recorrer cada resultado de la API
  apiResults.forEach(({ numeros, created_at: apiDate }) => {
    const apiDateObj = new Date(apiDate);
    // Para cada usuario con predicciones
    Object.entries(predictionsMap).forEach(([username, userPredictions]) => {
      userPredictions.forEach(({ userNumbers, predictionDate }) => {
        const predictionDateObj = new Date(predictionDate);
        if (predictionDateObj > apiDateObj) return;
      
        let dailyScore = 0;
        const aciertosToday = [];
        const matchedNumbers = new Set();
      
        numeros.forEach((lotNum, index) => {
          userNumbers.forEach(({ original, flipped }) => {
            if (!matchedNumbers.has(original) && (lotNum === original || lotNum === flipped)) {
              matchedNumbers.add(original);
              const positions = ['En Primera', 'En Segunda', 'En Tercera'];
              const posText = positions[index];
      
              if (lotNum === original) dailyScore += [1, 0.75, 0.5][index];
              else dailyScore += 0.5;
      
              aciertosToday.push({
                num: original,
                loteria: posText,
                description: lotNum === flipped ? 'Volteado' : '',
              });
            }
          });
        });
      
        if (!statsMap[username]) {
          statsMap[username] = { username, avatarUrl: '', totalScore: dailyScore, aciertosToday };
        } else {
          statsMap[username].totalScore += dailyScore;
          statsMap[username].aciertosToday.push(...aciertosToday);
        }
      });
    });
  });
  return Object.values(statsMap);
};

const WeeklyStatistics = () => {
  const [statistics, setStatistics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const resetExecutedRef = useRef(false); // Para evitar múltiples reinicios en la misma ventana

  // Función para resetear las estadísticas semanales en Firestore
  const resetWeeklyStatistics = async () => {
    try {
      const statsSnapshot = await getDocs(collection(db, 'weeklyStatistics'));
      const resetPromises = statsSnapshot.docs.map((docRef) => {
        const statDoc = doc(db, 'weeklyStatistics', docRef.id);
        return setDoc(statDoc, { username: '', score: 0, totalScore: 0, aciertos: [] });
      });
      await Promise.all(resetPromises);
      console.log('Estadísticas semanales restablecidas.');
    } catch (err) {
      console.error('Error al restablecer estadísticas:', err);
    }
  };

  // Verifica si es momento de resetear las estadísticas (se ejecuta solo una vez en la ventana)
  const checkResetTime = useCallback(() => {
    const now = new Date();
    // Si es sábado y la hora es 00:01
    if (now.getDay() === 6 && now.getHours() === 0 && now.getMinutes() === 1) {
      if (!resetExecutedRef.current) {
        resetExecutedRef.current = true;
        resetWeeklyStatistics();
      }
    } else {
      resetExecutedRef.current = false; // Reinicia el flag fuera de la ventana de reset
    }
  }, []);

  useEffect(() => {
    const intervalId = setInterval(checkResetTime, 60000);
    return () => clearInterval(intervalId);
  }, [checkResetTime]);

  // Función para obtener y procesar las estadísticas
  const fetchStatistics = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const currentDate = getCurrentDateInRD();
  
      const [weeklyStatsSnapshot, predictionsSnapshot, usersSnapshot] = await Promise.all([
        getDocs(collection(db, 'weeklyStatistics')),
        getDocs(collection(db, 'predictions')),
        getDocs(collection(db, 'users')),
      ]);
  
      const usersMap = usersSnapshot.docs.reduce((map, doc) => {
        map[doc.id] = doc.data().avatarUrl || '';
        return map;
      }, {});
  
      const savedStatistics = weeklyStatsSnapshot.docs.map((doc) => ({
        id: doc.id,
        username: doc.data().username,
        avatarUrl: usersMap[doc.data().username] || '',
        totalScore: doc.data().score || 0,
        aciertos: Array.isArray(doc.data().aciertos) ? doc.data().aciertos : [],
      }));
  
      const predictions = predictionsSnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          username: data.username || '',
          numbers: data.numbers ? data.numbers.split('-').map(Number) : [],
          timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : new Date(),
        };
      });
  
      const predictionsMap = groupPredictionsByUser(predictions, currentDate);
      const { data: apiData } = await axios.get(API_URL);
      const apiResults = processApiResults(apiData, currentDate);
  
      const combinedStats = calculateStatistics(savedStatistics, predictionsMap, apiResults);
  
      const updatePromises = combinedStats.map((stat) =>
        setDoc(doc(db, 'weeklyStatistics', stat.username), {
          username: stat.username,
          score: stat.totalScore,
          aciertos: stat.aciertosToday,
        }, { merge: true })
      );
  
      await Promise.all(updatePromises);
  
      const maxScore = Math.max(...combinedStats.map(stat => stat.totalScore), 1.4);
      const sortedStats = combinedStats.map(stat => ({
        ...stat,
        progress: (stat.totalScore / maxScore) * 100,
      })).sort((a, b) => b.totalScore - a.totalScore);
  
      setStatistics(sortedStats);
    } catch (err) {
      console.error('Error al cargar estadísticas:', err.message, err);
      setError('No se pudieron cargar los datos. Inténtalo más tarde.');
    } finally {
      setLoading(false);
    }
  }, []);
  

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  // Función para determinar el color de la barra de progreso
  const getProgressColor = (progress) => {
    if (progress >= 75) return '#4caf50';
    if (progress >= 50) return '#ffc107';
    return '#f44336';
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" align="center" gutterBottom>
        Estadísticas Semanales
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Button variant="outlined" onClick={fetchStatistics}>
          Actualizar Estadísticas
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : statistics.length === 0 ? (
        <Typography align="center" color="textSecondary">
          No hay estadísticas disponibles para mostrar.
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {statistics.map((user, index) => (
            <Grid item xs={12} sm={6} md={4} key={user.id || user.username}>
              <Card
                sx={{
                  border: `3px solid ${user.dailyScore > 0 ? 'green' : 'red'}`,
                  borderRadius: 3,
                  boxShadow: 3,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Avatar src={user.avatarUrl || ''} alt={user.username} />
                    <Typography variant="h6" sx={{ fontSize: '1.3rem' }}>
                      #{index + 1} {user.username}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Puntaje Total: {user.totalScore.toFixed(2)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontWeight: 'bold',
                      backgroundColor: '#f0f4c3',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      display: 'inline-block',
                      fontSize: '1.1rem',
                    }}
                  >
                    Puntaje Total: {user.totalScore.toFixed(2)}
                  </Typography>
                  {user.aciertosToday.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1, fontSize: '1.1rem' }}>
                        Aciertos de Hoy: {user.aciertosToday.length}
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, m: 0 }}>
                      {user.aciertosToday.map((acierto, i) => (
  <li key={i} style={{ fontSize: '0.95rem' }}>
    {acierto.loteria} - Número: {acierto.num} {acierto.description && `(${acierto.description})`}
  </li>
))}
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ flexGrow: 1, position: 'relative' }}>
                      <LinearProgress
                        variant="determinate"
                        value={user.progress}
                        sx={{
                          height: 15,
                          borderRadius: 10,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getProgressColor(user.progress),
                            transition: 'width 0.4s ease',
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          fontWeight: 'bold',
                          fontSize: '12px',
                          color: '#000',
                        }}
                      >
                        {Math.round(user.progress)}%
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Box
        sx={{
          mt: 5,
          display: 'flex',
          justifyContent: 'center',
          gap: 3,
          flexWrap: 'wrap',
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/chat')}
          sx={{ px: 4, py: 1.5, fontWeight: 'bold', fontSize: '16px', borderRadius: '8px' }}
        >
          Volver al Chat
        </Button>
      </Box>
    </Container>
  );
};

export default WeeklyStatistics;
